// ---------------------------------------------------------------------------
// HTTP calls. The lobby browser and profiles are plain requests; everything
// inside a room goes over the socket instead.
// ---------------------------------------------------------------------------

import type {
  ClaimDeviceGamesResponse,
  CreateRoomRequest,
  CreateRoomResponse,
  GameRecordResponse,
  MeResponse,
  ModelProfileResponse,
  ModelSummary,
  ProfileResponse,
  PublicRoomSummary,
  SetUsernameResponse,
} from '@countdown/shared';

export interface SiteConfig {
  buildId?: string;
  turnstileSiteKey: string | null;
  turnstileEnabled: boolean;
  clerkPublishableKey: string | null;
  clerkEnabled: boolean;
  /**
   * A restart the server has already announced, or null.
   *
   * It rides on the config because the config is the one thing every client
   * fetches on every page load. The announcement itself is a socket broadcast,
   * which reaches only people who are already sitting in a room and does not
   * survive their reloading the page — so a player who refreshed, and anybody
   * who was on the home screen when it went out, had no idea the server was
   * about to go down under them.
   *
   * Optional so a server that has not shipped the field yet reads as "nothing
   * announced" rather than breaking the page for everyone.
   */
  maintenance?: { message: string; deployAt: number | null } | null;
  /**
   * AI opponents, and the models this server will accept.
   *
   * The catalog is served rather than bundled so that adding or retiring a
   * model is a server change. A client holding a stale list would offer
   * something the server refuses, and the player would read that as the button
   * being broken.
   *
   * Optional for the same reason `maintenance` is: a server that predates the
   * field reads as "no AI opponents" instead of breaking the lobby.
   */
  llm?: {
    enabled: boolean;
    models: LlmModelOption[];
    defaultModel: string;
    /** The month's budget is spent. Explains a disabled control; never the gate itself. */
    budgetExhausted: boolean;
  };
}

export interface LlmModelOption {
  id: string;
  label: string;
  /**
   * What a seat on this model is CALLED, which is not what the picker shows.
   * The picker is a list of models and shows `label`; the short name is for the
   * places a name has to fit — a player chip, the standings strip, a running
   * order pill. The server names the seat, so nothing here reads this. It is
   * declared because the payload carries it and a type that quietly omits a
   * field it receives is a type nobody can trust.
   */
  shortName: string;
  note: string;
  inputPerMillion: number;
  outputPerMillion: number;
  /**
   * What one call to this model usually costs in tokens, measured server-side.
   * With the two prices above, this is everything needed to quote a seat.
   *
   * Optional because a server that predates the field must not break the
   * picker: `seatCostPerGame` returns null and the row simply shows no price,
   * which is the same thing it showed before there was one.
   */
  usualTokens?: { prompt: number; completion: number };
  /**
   * Wall time to a whole answer, in milliseconds, measured server-side over six
   * boards. Not time to first token — see the doc comment on the field in
   * `backend/src/llm/models.ts`, which is emphatic about the difference: by the
   * first-token figure `gpt-oss-120b` is the fastest model in the catalogue at
   * 494ms while taking 43 seconds to finish a one-word answer.
   *
   * It is an AVERAGE, so half of real calls are slower than it, and the lobby's
   * warning is written to say so rather than quoting it as a deadline.
   *
   * Optional for the same reason `usualTokens` is: a server that predates the
   * field says nothing about the clock instead of guessing at it.
   */
  usualLatencyMs?: number;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });

  if (!response.ok) {
    // The server sends a human-readable reason for everything a player can hit.
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Request failed (${response.status})`);
  }

  return (await response.json()) as T;
}

export function getConfig(): Promise<SiteConfig> {
  return request<SiteConfig>('/api/config');
}

export function listPublicRooms(): Promise<PublicRoomSummary[]> {
  return request<PublicRoomSummary[]>('/api/rooms');
}

/**
 * One room by code, whether or not it is listed. The response is exactly the
 * browse list's own summary — it carried one extra field, `locked`, and the
 * room lock is gone.
 */
export function lookupRoom(code: string): Promise<PublicRoomSummary> {
  return request<PublicRoomSummary>(`/api/rooms/${encodeURIComponent(code)}`);
}

export function createRoom(body: CreateRoomRequest): Promise<CreateRoomResponse> {
  return request<CreateRoomResponse>('/api/rooms', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getProfile(username: string): Promise<ProfileResponse> {
  return request<ProfileResponse>(`/api/u/${encodeURIComponent(username)}`);
}

/**
 * One finished game, round by round. Public: anyone with the link can read it.
 *
 * The id is the server's own `randomUUID()`, so it is not guessable and the
 * route is unauthenticated.
 */
export function getGameRecord(gameId: string): Promise<GameRecordResponse> {
  return request<GameRecordResponse>(`/api/games/${encodeURIComponent(gameId)}`);
}

/** Every AI opponent that has played, plus every one the lobby offers. */
export function listModels(): Promise<ModelSummary[]> {
  return request<ModelSummary[]>('/api/models');
}

/**
 * One AI opponent's profile.
 *
 * The id goes over as two path segments rather than one encoded string: an
 * OpenRouter id is `provider/name`, and `%2F` in a path is mangled by enough
 * proxies to be worth avoiding. `modelPath` in `format.ts` builds the matching
 * client-side route and declines ids that will not round-trip.
 */
export function getModelProfile(modelId: string): Promise<ModelProfileResponse> {
  const [provider, name] = modelId.split('/');
  return request<ModelProfileResponse>(
    `/api/models/${encodeURIComponent(provider)}/${encodeURIComponent(name)}`,
  );
}

export function getLeaderboard(): Promise<
  { username: string; displayName: string; wins: number; totalScore: number; gamesPlayed: number }[]
> {
  return request('/api/leaderboard');
}

// --- Accounts --------------------------------------------------------------
// Every route here carries the Clerk session JWT and answers 401 when it does
// not verify. The token is a parameter rather than something this module reads:
// only the caller holds a live session, and a JWT is short-lived enough that one
// fetched a few minutes ago is already worthless.

/** The signed-in half of shared's `MeResponse`, without the discriminant. */
export type Account = Omit<Extract<MeResponse, { signedIn: true }>, 'signedIn'>;

function authed(token: string, init?: RequestInit): RequestInit {
  return { ...init, headers: { Authorization: `Bearer ${token}`, ...(init?.headers ?? {}) } };
}

/**
 * The device id is what makes `claimableGames` answerable: guest games are
 * recorded against the device, and the server counts nothing without being told
 * which one to count.
 */
export function getMe(token: string, deviceId: string): Promise<MeResponse> {
  return request<MeResponse>(`/api/me?deviceId=${encodeURIComponent(deviceId)}`, authed(token));
}

/**
 * Claim a permanent username. The rules live in `validateUsername` in shared and
 * are enforced by the server; a 400, 409 or 429 comes back with the sentence
 * that says which one was broken, and `request` throws it verbatim.
 */
export function claimUsername(
  token: string,
  body: { username: string; displayName?: string },
): Promise<SetUsernameResponse> {
  return request<SetUsernameResponse>(
    '/api/me/username',
    authed(token, { method: 'POST', body: JSON.stringify(body) }),
  );
}

/** Adopt the guest games recorded against this device. Never called unprompted. */
export function claimGuestGames(
  token: string,
  deviceId: string,
): Promise<ClaimDeviceGamesResponse> {
  return request<ClaimDeviceGamesResponse>(
    '/api/me/claim-games',
    authed(token, { method: 'POST', body: JSON.stringify({ deviceId }) }),
  );
}
